import { apiRequest } from './api';

export type HomeworkPayload = {
    class_id: string;
    section_id: string | null;
    school_subject_id: string;
    title: string;
    description: string | null;
    due_date: string;
    total_marks: number | null;
};

export type HomeworkApiRecord = {
    id: string;
    organization_id: string;
    class_id: string;
    section_id: string | null;
    subject_id: string;
    title: string;
    description: string | null;
    due_date: string;
    total_marks: string | null;
    created_at: string;
    updated_at: string;
    teacher?: { id: string; first_name: string; last_name: string } | null;
    school_class?: { id: string; name: string; standard?: number } | null;
    section?: { id: string; name: string } | null;
    subject?: { id: string; board_subject_name: string } | null;
};

export type HomeworkListResponse = {
    success: boolean;
    message: string;
    data: HomeworkApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

export async function fetchHomeworks(params?: {
    page?: number;
    perPage?: number;
    classId?: string;
    sectionId?: string;
}): Promise<HomeworkListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.classId) query.set('class_id', params.classId);
    if (params?.sectionId) query.set('section_id', params.sectionId);
    const qs = query.toString();
    const res = await apiRequest<HomeworkApiRecord[]>(`/api/homeworks${qs ? `?${qs}` : ''}`);
    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as HomeworkListResponse).meta,
    };
}

export async function createHomework(payload: HomeworkPayload): Promise<HomeworkApiRecord> {
    const res = await apiRequest<HomeworkApiRecord>('/api/homeworks', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

export type SubmissionStatusValue = 'pending' | 'submitted' | 'evaluated' | 'late';

export type SubmissionStatusRecord = {
    student_id: string;
    status: SubmissionStatusValue;
};

export async function fetchHomeworkSubmissionStatuses(
    homeworkId: string
): Promise<SubmissionStatusRecord[]> {
    const res = await apiRequest<SubmissionStatusRecord[]>(
        `/api/homeworks/${homeworkId}/submissions/statuses`
    );
    return res.data;
}

export type RecordGradePayload = {
    student_id: string;
    class_id: string;
    section_id: string;
    marks_obtained: number | null;
    teacher_feedback: string;
    status: SubmissionStatusValue;
};

export async function recordHomeworkGrade(
    homeworkId: string,
    payload: RecordGradePayload
): Promise<void> {
    await apiRequest(`/api/homeworks/${homeworkId}/submissions/record-grade`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}
